// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useState, type FormEvent } from "react";
import ArrowLeft from "~icons/ph/arrow-left";
import Phone from "~icons/ph/phone";
import UserPlus from "~icons/ph/user-plus";
import Video from "~icons/ph/video-camera";
import type { CallMedia, Guest, Person } from "../../shared/types";
import { useShell } from "../app/shell";
import { useKeep, useSubmitting } from "../data/mutations";
import { useGuest } from "../data/session";
import { useConnectionAction } from "../people/person-row";
import { ActivityStatus, Avatar, Modal } from "../ui/components";
import { CallHeading } from "./call-heading";
import { t } from "../i18n";

// Everything a call-link guest has: their host, ringing again until the pass
// ends, and choosing to stay in touch or leave. During a call, staying in
// touch takes the place of ringing again.
export function GuestCall({
  guest,
  busy,
  calling,
  onCall,
  onRetry,
  onKeep,
  onLeave,
  onReport,
}: {
  guest: Guest;
  busy: boolean;
  calling: boolean;
  onCall: (media: CallMedia) => void;
  onRetry: () => void;
  onKeep: () => void;
  onLeave: () => void;
  onReport: () => void;
}) {
  const { host, outcome } = guest;
  // A host with call notifications on gets one when the guest calls.
  const away = host.status !== "online",
    available = !away || !!host.call_notifications;
  const keep = (
    <button
      className="button"
      disabled={busy}
      aria-busy={busy}
      onClick={onKeep}
    >
      {t("ابقَ على تواصل")}
    </button>
  );
  return (
    <div className="call-invite guest-call">
      <Avatar person={host} large dot />
      {outcome === "declined" ? (
        <>
          <h1 id="invite-title">{t("رُفضت المكالمة")}</h1>
          <p className="call-invite-lead">
            {t("انتهى هذا الرابط. اطلب رابطًا جديدًا ممن أرسله إليك.")}
          </p>
        </>
      ) : outcome === "talked" || calling ? (
        <>
          <CallHeading name={host.name}>
            {calling ? "في مكالمة مع {name}" : "انتهت المكالمة مع {name}"}
          </CallHeading>
          <p className="call-invite-lead">
            {t(
              "ابقَ على تواصل لتتصلا مجددًا لاحقًا. ستحصل على حساب ويمكنك إضافة {name} إلى أصدقائك.",
              { name: host.name },
            )}
          </p>
          <div className="call-invite-actions">{keep}</div>
        </>
      ) : (
        <>
          <CallHeading name={host.name}>{"اتصل بـ{name} مجددًا"}</CallHeading>
          <p className="call-invite-host">
            <bdi dir="ltr">@{host.username}</bdi>
            <span aria-hidden="true">·</span>
            <span className={`person-activity ${host.status}`}>
              <ActivityStatus person={host} />
            </span>
          </p>
          <div className="call-invite-card">
            {!available ? (
              <p className="call-invite-away" role="status">
                {t("{name} غير متاح حاليًا. أبقِ الصفحة مفتوحة وتحقق بعد قليل.", {
                  name: host.name,
                })}{" "}
                <button
                  type="button"
                  className="quiet-button"
                  onClick={onRetry}
                >
                  {t("تحقق مجددًا")}
                </button>
              </p>
            ) : (
              away && (
                <p className="call-invite-away" role="status">
                  {t("سيصل إشعار إلى {name} عند اتصالك.", { name: host.name })}
                </p>
              )
            )}
            <div className="call-invite-actions">
              {(["audio", "video"] as const).map((media) => (
                <button
                  key={media}
                  className={`button ${media === "video" ? "secondary" : ""}`}
                  aria-busy={busy}
                  disabled={busy || calling || !available}
                  onClick={() => onCall(media)}
                >
                  {media === "video" ? (
                    <Video width={18} height={18} aria-hidden="true" />
                  ) : (
                    <Phone width={18} height={18} aria-hidden="true" />
                  )}
                  {t(media === "video" ? "مكالمة فيديو" : "مكالمة صوتية")}
                </button>
              ))}
            </div>
            <p className="call-invite-note">
              {t("يمكنك الاتصال مجددًا حتى تنتهي صلاحية الرابط.")}
            </p>
          </div>
          <div className="call-invite-actions">{keep}</div>
        </>
      )}
      <button className="quiet-button" onClick={onReport} disabled={busy}>
        {t("الإبلاغ عن {name}", { name: host.name })}
      </button>
      {/* Leaving signs the guest out, which would drop the call unasked. */}
      {!calling && (
        <button className="restore-link" disabled={busy} onClick={onLeave}>
          {t("مغادرة")}
        </button>
      )}
    </div>
  );
}

// Staying in touch keeps the name the guest joined with unless they change
// it; skipping also leaves the username to the server.
function KeepInTouch({
  name: joinedName,
  disabled,
  error,
  onKeep,
}: {
  name: string;
  disabled: boolean;
  error: string;
  onKeep: (changes: { name?: string; username?: string }) => void;
}) {
  const [name, setName] = useState(joinedName);
  const [username, setUsername] = useState("");
  function submit(event: FormEvent) {
    event.preventDefault();
    onKeep({ name, username: username.trim() || undefined });
  }
  return (
    <>
      <h2>{t("جهّز حسابك")}</h2>
      <p className="modal-description">
        {t("احتفظ بالاسم الذي انضممت به أو غيّره، واختر اسم مستخدم إن أردت.")}
      </p>
      <form onSubmit={submit}>
        <label>
          {t("اسمك")}{" "}
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            minLength={2}
            maxLength={40}
            required
            autoComplete="nickname"
          />
        </label>
        <label>
          {t("اسم المستخدم")}{" "}
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            data-autofocus
            aria-label={t("اسم المستخدم")}
            aria-describedby="keep-username-help"
            pattern="[a-z0-9_]{3,24}"
            minLength={3}
            maxLength={24}
            placeholder="your_username"
            dir="ltr"
            autoComplete="username"
          />
          <small id="keep-username-help" className="field-help">
            {t(
              "اختياري. حروف إنجليزية وأرقام وشرطة سفلية، وإن تركته فارغًا فسنختار لك واحدًا.",
            )}
          </small>
        </label>
        {error && (
          <p className="form-error" role="alert">
            {t(error)}
          </p>
        )}
        <div className="confirm-actions">
          <button
            type="button"
            className="button secondary"
            disabled={disabled}
            onClick={() => onKeep({})}
          >
            {t("تخطي")}
          </button>
          <button className="button" aria-busy={disabled} disabled={disabled}>
            {t("متابعة")}{" "}
            <ArrowLeft className="directional" width={16} height={16} />
          </button>
        </div>
      </form>
    </>
  );
}

// The new account asks the host to be friends only if the guest wants to.
function AddHost({
  host,
  disabled,
  onRequest,
  onSkip,
}: {
  host: Person;
  disabled: boolean;
  onRequest: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      {host.avatar && <Avatar person={host} large />}
      <h2>{t("إضافة {name} إلى أصدقائك؟", { name: host.name })}</h2>
      <p className="modal-description">
        {t(
          "حسابك جاهز. أرسل طلب إضافة إلى {name} لتتمكنا من الاتصال والمراسلة بعد قبوله.",
          { name: host.name },
        )}
      </p>
      <div className="confirm-actions">
        <button
          className="button secondary"
          disabled={disabled}
          onClick={onSkip}
        >
          {t("تخطي")}
        </button>
        <button
          className="button"
          data-autofocus
          aria-busy={disabled}
          disabled={disabled}
          onClick={onRequest}
        >
          <UserPlus width={16} height={16} aria-hidden="true" />
          {t("إرسال طلب إضافة")}
        </button>
      </div>
    </>
  );
}

// A guest who keeps in touch gets an account, then may add their host.
export function KeepDialog() {
  const { close } = useShell();
  const guest = useGuest();
  const [host] = useState(guest?.host);
  const keep = useKeep();
  const requestAction = useConnectionAction();
  const submitting = useSubmitting();
  const [kept, setKept] = useState(false),
    [error, setError] = useState("");
  async function keepInTouch(changes: { name?: string; username?: string }) {
    setError("");
    try {
      await keep.mutateAsync(changes);
      setKept(true);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <Modal
      key={kept ? "add-host" : "keep"}
      title={
        kept && host
          ? t("إضافة {name} إلى أصدقائك؟", { name: host.name })
          : t("ابقَ على تواصل")
      }
      onClose={close}
    >
      {kept && host ? (
        <AddHost
          host={host}
          disabled={submitting}
          onRequest={async () => {
            if (await requestAction(host, "request")) close();
          }}
          onSkip={close}
        />
      ) : (
        <KeepInTouch
          name={guest?.name ?? ""}
          disabled={submitting}
          error={error}
          onKeep={(changes) => void keepInTouch(changes)}
        />
      )}
    </Modal>
  );
}
